import type { BlockNoteEditor, PartialBlock } from '@blocknote/core';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';
import * as React from 'react';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/shadcn/style.css';

import {
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useTheme,
} from '@heartplus/ui';

import { createSchemaWithDropdown } from '../../blocknote-schema';
import { BlockNoteErrorBoundary } from '../blocknote-error-boundary';
import { BlockNoteFormattingToolbar } from './blocknote-formatting-toolbar';
import { BlockNoteSlashMenu } from './blocknote-slash-menu';
import { normalizeInitialEditorContent } from './content-normalization';
import type { BlockNoteEditorWithDropdownProps } from './types';
import { useDropdownBlockRestoration } from './use-dropdown-block-restoration';
import { useSelectionAutoScroll } from './use-selection-auto-scroll';
import { useTableVariantTagging, buildTableVariantMap, applyTableVariants } from './use-table-variant-tagging';

/**
 * Renders a BlockNote editor configured with the shared dropdown, date,
 * horizontal rule, token inline, formatting toolbar, and custom slash menu
 * behavior used by note/document editing surfaces.
 */
export const BlockNoteEditorWithDropdown = React.forwardRef<
  BlockNoteEditor,
  BlockNoteEditorWithDropdownProps
>(
  (
    {
      initialContent,
      onChange,
      onInitialContentSettled,
      editable = true,
      className,
      placeholder,
      theme,
      getCustomSlashMenuItems: getCustomSlashMenuItemsProp,
      ...props
    },
    ref,
  ) => {
    const { resolvedTheme } = useTheme();
    const editorTheme = theme || resolvedTheme;
    // Preserve the placeholder prop contract; this editor path has never
    // forwarded it to BlockNote initialization.
    void placeholder;

    // Custom block renderers need a stable getter because BlockNote does not
    // always update editor.isEditable when BlockNoteView.editable changes.
    const editableRef = React.useRef(editable);
    const wrapperRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      editableRef.current = editable;
    }, [editable]);

    const schema = React.useMemo(() => {
      return createSchemaWithDropdown(
        {
          Select,
          SelectContent,
          SelectItem,
          SelectTrigger,
          SelectValue,
        },
        {
          getIsEditable: () => editableRef.current,
        },
      );
    }, []);

    const filteredInitialContent = React.useMemo(
      () => normalizeInitialEditorContent(initialContent, editable),
      [editable, initialContent],
    );

    const editor = useCreateBlockNote({
      schema,
      initialContent: filteredInitialContent,
    });
    const blockNoteEditor = editor as BlockNoteEditor;
    const isRestoringDropdownsRef = React.useRef(false);

    const tableVariantMap = React.useMemo(
      () => buildTableVariantMap(filteredInitialContent),
      [filteredInitialContent],
    );

    const handleChange = React.useCallback(() => {
      if (isRestoringDropdownsRef.current) {
        return;
      }

      try {
        const blocks = blockNoteEditor.document as PartialBlock[];
        onChange?.(applyTableVariants(blocks, tableVariantMap));
      } catch (error) {
        console.warn('Error in handleChange:', error);
      }
    }, [blockNoteEditor, onChange, tableVariantMap]);

    useDropdownBlockRestoration({
      editor: blockNoteEditor,
      initialContent,
      isRestoringDropdownsRef,
      editable,
      // Fires once the initial content has settled — immediately when there are
      // no deferred blocks, or after deferred custom blocks are restored. Lets
      // consumers re-baseline against the editor's own representation instead of
      // the persisted format (no restoration-timer race).
      onSettled: onInitialContentSettled,
    });
    useSelectionAutoScroll(blockNoteEditor);
    useTableVariantTagging(wrapperRef, filteredInitialContent);

    React.useImperativeHandle(ref, () => blockNoteEditor, [blockNoteEditor]);

    const getSlashMenuItems = getCustomSlashMenuItemsProp
      ? getCustomSlashMenuItemsProp(blockNoteEditor)
      : undefined;

    return (
      <div
        ref={wrapperRef}
        className={cn('blocknote-editor-wrapper bg-transparent', className)}
        {...props}
      >
        <style>
          {`
            .blocknote-editor-wrapper table:not([data-table-variant='borderless']) {
              border-collapse: collapse;
              border: 1px solid hsl(var(--border));
            }

            .blocknote-editor-wrapper table:not([data-table-variant='borderless']) td,
            .blocknote-editor-wrapper table:not([data-table-variant='borderless']) th {
              border: 1px solid hsl(var(--border));
            }

            .blocknote-editor-wrapper table[data-table-variant='borderless'],
            .blocknote-editor-wrapper table[data-table-variant='borderless'] tbody,
            .blocknote-editor-wrapper table[data-table-variant='borderless'] tr,
            .blocknote-editor-wrapper table[data-table-variant='borderless'] td,
            .blocknote-editor-wrapper table[data-table-variant='borderless'] th,
            .blocknote-editor-wrapper table[data-table-variant='borderless'] * {
              border: 0 !important;
              box-shadow: none !important;
            }
          `}
        </style>
        <BlockNoteErrorBoundary
          fallback={() => (
            <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
              Editor temporarily unavailable. Please refresh the page.
            </div>
          )}
        >
          <BlockNoteView
            editor={editor}
            editable={editable}
            onChange={handleChange}
            slashMenu={false}
            formattingToolbar={false}
            theme={editorTheme as 'light' | 'dark'}
          >
            <BlockNoteFormattingToolbar />
            <BlockNoteSlashMenu getItems={getSlashMenuItems} />
          </BlockNoteView>
        </BlockNoteErrorBoundary>
      </div>
    );
  },
);

BlockNoteEditorWithDropdown.displayName = 'BlockNoteEditorWithDropdown';
